/**
 * CataloguePage — the reagent catalog: the Magasinier's home. Full reactif CRUD (create + edit),
 * per-row lot reception, the FEFO/expiry/risk drawer, lot recall launch, and the idempotent expiry
 * sweep. Drives the full 5-state DoD (vide / chargement / erreur / hors-ligne / sans-droit).
 *
 * Gating: the module route is ProtectedRoute'd on inventory.reactif.read; per-action buttons are gated
 * on their exact server capability via useCan (create/update/lot.receive/mouvement.create/recall.run).
 * A defensive ForbiddenState covers a lost read capability.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  Input,
  LoadingState,
  OfflineState,
  Table,
  type Column,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { INV_CAPS } from "../caps";
import { useLabId, useReactifs } from "../api";
import type { Reactif, RiskLot } from "../types";
import { idStr } from "../types";
import { fmtQty } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { ReactifFormModal } from "../components/ReactifFormModal";
import { ReceiveLotModal } from "../components/ReceiveLotModal";
import { LotsDrawer } from "../components/LotsDrawer";
import { SweepModal } from "../components/SweepModal";

export function CataloguePage() {
  const { t } = useT();
  const navigate = useNavigate();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(INV_CAPS.reactifRead);
  const canCreate = useCan(INV_CAPS.reactifCreate);
  const canUpdate = useCan(INV_CAPS.reactifUpdate);
  const canReceive = useCan(INV_CAPS.lotReceive);
  const canRecall = useCan(INV_CAPS.recallRun);
  const canSweep = useCan(INV_CAPS.mouvementCreate);

  const query = useReactifs(labId, canRead && online);
  const reactifs = useMemo(() => query.data ?? [], [query.data]);

  const [search, setSearch] = useState("");
  const [formReactif, setFormReactif] = useState<Reactif | null | undefined>(undefined); // undefined = closed
  const [receiveReactif, setReceiveReactif] = useState<Reactif | null>(null);
  const [lotsReactif, setLotsReactif] = useState<Reactif | null>(null);
  const [sweepOpen, setSweepOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reactifs;
    return reactifs.filter(
      (r) =>
        r.code.toLowerCase().includes(q) ||
        r.libelle.toLowerCase().includes(q) ||
        r.discipline_code.toLowerCase().includes(q),
    );
  }, [reactifs, search]);

  const openRecall = (reactif: Reactif, lot?: RiskLot) => {
    navigate("/inventory/recall", {
      state: {
        reactifId: idStr(reactif.id),
        ...(lot ? { lotId: idStr(lot.lot_id), numeroLot: lot.numero_lot } : {}),
      },
    });
  };

  const columns: ReadonlyArray<Column<Reactif>> = [
    { key: "code", header: t("inventory.catalogue.col.code"), mono: true, sortable: true, sortAccessor: (r) => r.code, cell: (r) => r.code },
    { key: "libelle", header: t("inventory.catalogue.col.libelle"), sortable: true, sortAccessor: (r) => r.libelle, cell: (r) => r.libelle },
    { key: "discipline", header: t("inventory.catalogue.col.discipline"), mono: true, cell: (r) => r.discipline_code },
    {
      key: "unite",
      header: t("inventory.catalogue.col.unite"),
      cell: (r) => t(`inventory.unit.${r.unite_mesure ?? "test"}`),
    },
    {
      key: "seuil",
      header: t("inventory.catalogue.col.seuil"),
      mono: true,
      align: "end",
      cell: (r) => <span className="nv-ltr-island">{fmtQty(r.seuil_alerte)}</span>,
    },
    {
      key: "statut",
      header: t("inventory.catalogue.col.statut"),
      cell: (r) =>
        r.statut === "inactif" ? (
          <Badge tone="neutral" glyph="○">
            {t("inventory.catalogue.statut.inactif")}
          </Badge>
        ) : (
          <Badge tone="success" glyph="●">
            {t("inventory.catalogue.statut.actif")}
          </Badge>
        ),
    },
    {
      key: "actions",
      header: t("inventory.catalogue.col.actions"),
      align: "end",
      cell: (r) => (
        <div style={{ display: "inline-flex", gap: "var(--nv-s-2)", justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button size="sm" variant="ghost" onClick={() => setLotsReactif(r)}>
            {t("inventory.catalogue.action.lots")}
          </Button>
          <Button size="sm" variant="secondary" disabled={!canReceive} onClick={() => setReceiveReactif(r)}>
            {t("inventory.catalogue.action.receive")}
          </Button>
          <Button size="sm" variant="ghost" disabled={!canUpdate} onClick={() => setFormReactif(r)}>
            {t("inventory.catalogue.action.edit")}
          </Button>
        </div>
      ),
    },
  ];

  const header = (
    <PageHeader
      title={t("inventory.catalogue.title")}
      subtitle={t("inventory.catalogue.subtitle")}
      actions={
        <>
          <Button variant="secondary" disabled={!canSweep} onClick={() => setSweepOpen(true)}>
            {t("inventory.sweep.cta")}
          </Button>
          <Button disabled={!canCreate} onClick={() => setFormReactif(null)}>
            {t("inventory.catalogue.new")}
          </Button>
        </>
      }
    />
  );

  // 5-state DoD, in priority order.
  if (!canRead) return <ForbiddenState requiredCapabilities={[INV_CAPS.reactifRead]} />;

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={reactifs.length > 0} />
      </Screen>
    );
  }
  if (query.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={6} />
      </Screen>
    );
  }
  if (query.isError) {
    return (
      <Screen>
        {header}
        <ErrorState message={query.error.message} code={query.error.code} onRetry={() => void query.refetch()} />
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      <Card padded={false}>
        <div style={{ padding: "var(--nv-s-3)", borderBottom: "1px solid var(--nv-line-faint)" }}>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("inventory.catalogue.search")}
            aria-label={t("inventory.catalogue.search")}
          />
        </div>
        <div style={{ padding: "var(--nv-s-3)" }}>
          <Table
            columns={columns}
            rows={filtered}
            rowKey={(r) => idStr(r.id)}
            caption={t("inventory.catalogue.title")}
            pageSize={12}
            emptyTitle={
              search.trim()
                ? t("inventory.catalogue.empty.search")
                : t("inventory.catalogue.empty.title")
            }
            {...(search.trim() ? {} : { emptyDescription: t("inventory.catalogue.empty.desc") })}
            emptyAction={
              !search.trim() && canCreate ? (
                <Button onClick={() => setFormReactif(null)}>{t("inventory.catalogue.new")}</Button>
              ) : undefined
            }
          />
        </div>
      </Card>

      <ReactifFormModal
        open={formReactif !== undefined}
        reactif={formReactif ?? null}
        onClose={() => setFormReactif(undefined)}
        onSaved={() => setFormReactif(undefined)}
      />

      <ReceiveLotModal
        open={!!receiveReactif}
        reactif={receiveReactif}
        onClose={() => setReceiveReactif(null)}
        onReceived={() => setReceiveReactif(null)}
      />

      <LotsDrawer
        open={!!lotsReactif}
        reactif={lotsReactif}
        onClose={() => setLotsReactif(null)}
        onRecall={(lot) => {
          if (lotsReactif && canRecall) openRecall(lotsReactif, lot);
        }}
      />

      <SweepModal open={sweepOpen} onClose={() => setSweepOpen(false)} onDone={() => setSweepOpen(false)} />
    </Screen>
  );
}

/** The shared vertical-stack frame each screen state shares. */
function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
